import { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Mic2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import { api } from '../lib/api';
import { useStudioEngine } from '../hooks/useStudioEngine';
import { useStudioShortcuts } from '../hooks/useStudioShortcuts';
import { Timeline } from '../components/studio/Timeline';
import { TransportBar } from '../components/studio/TransportBar';
import { InstrumentalPanel } from '../components/studio/InstrumentalPanel';
import { OrdersPanel } from '../components/studio/OrdersPanel';
import { ExportsPanel } from '../components/studio/ExportsPanel';
import { ExportDialog } from '../components/studio/ExportDialog';
import { LatencyCalibrationDialog } from '../components/studio/LatencyCalibrationDialog';
import { Button } from '../components/ui/button';
import { Upload } from 'lucide-react';
import { useRef } from 'react';
import { toast } from 'sonner';

const LS_LATENCY_PROMPTED = 't4t_latency_prompted';

export default function Studio() {
  const engine = useStudioEngine();
  const [artist, setArtist] = useState(null);
  const [instrumentals, setInstrumentals] = useState([]);
  const [orders, setOrders] = useState([]);
  const [exportsList, setExportsList] = useState([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const localFileRef = useRef(null);
  useStudioShortcuts(engine, exportOpen);

  const loadInstrumentals = useCallback(async () => {
    try { setInstrumentals((await api.getInstrumentals()).data); } catch (e) { /* noop */ }
  }, []);
  const loadOrders = useCallback(async () => {
    try { setOrders((await api.getArtistOrders()).data); } catch (e) { /* noop */ }
  }, []);
  const loadExports = useCallback(async () => {
    try { setExportsList((await api.getStudioExports()).data); } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    api.getArtistProfile().then((r) => setArtist(r.data)).catch(() => {});
    loadInstrumentals();
    loadOrders();
    loadExports();
  }, [loadInstrumentals, loadOrders, loadExports]);

  // Upfront mic + room check — requests mic permission and measures the
  // ambient noise floor as soon as the Studio opens, so the verdict is
  // already on screen and Record rolls instantly when pressed.
  useEffect(() => {
    engine.runNoiseCheck();
    // Intentionally mount-only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // First-visit calibration nudge — fires once per device, only if the artist
  // hasn't already calibrated. Dismissable; opening the dialog counts as the
  // "shown" event so we never nag them twice.
  useEffect(() => {
    const alreadyPrompted = (() => {
      try { return localStorage.getItem(LS_LATENCY_PROMPTED) === '1'; } catch (e) { return false; }
    })();
    const alreadyCalibrated = engine.latencyMs != null;
    if (alreadyPrompted || alreadyCalibrated) return;

    const markShown = () => {
      try { localStorage.setItem(LS_LATENCY_PROMPTED, '1'); } catch (e) { /* noop */ }
    };
    const timer = setTimeout(() => {
      toast('Tighten your timing in 2 seconds', {
        description: 'Calibrate your device once so recorded vocals land in the pocket. Takes a single tap.',
        duration: 12000,
        action: {
          label: 'Calibrate',
          onClick: () => { markShown(); setCalOpen(true); },
        },
        onDismiss: markShown,
        onAutoClose: markShown,
      });
    }, 1200);
    return () => clearTimeout(timer);
    // Intentionally only runs on the initial mount — deps left empty on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLocalFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      await engine.loadInstrumentalFromFile(file);
      toast.success(`Loaded "${file.name}" onto the timeline`);
    } catch (err) {
      toast.error('Could not read that audio file');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold flex items-center gap-3" data-testid="studio-title">
                <span className="w-11 h-11 bg-lime/10 rounded-xl flex items-center justify-center">
                  <Mic2 className="w-6 h-6 text-lime" />
                </span>
                STUDIO
              </h1>
              <p className="text-sm text-[#888] mt-1.5">
                {artist ? `Welcome, ${artist.name}. ` : ''}Load a riddim, record your take, export the stems.
              </p>
            </div>
            <Button variant="outline" className="border-[#333]" onClick={() => localFileRef.current?.click()} data-testid="load-local-instrumental-btn">
              <Upload className="w-4 h-4 mr-2" />Load instrumental from device
            </Button>
            <input ref={localFileRef} type="file" accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac,.aif,.aiff" className="hidden" onChange={handleLocalFile} data-testid="local-instrumental-input" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* DAW column */}
            <div className="lg:col-span-2 space-y-4">
              {engine.instrumental && (
                <p className="text-xs text-[#888]">
                  Instrumental: <span className="text-lime">{engine.instrumental.title}</span>
                  <span className="text-[#555]"> — drag the clip to set where the riddim starts</span>
                </p>
              )}
              <Timeline engine={engine} />
              <TransportBar
                engine={engine}
                canExport={!!(engine.vocal || engine.instrumental)}
                onExportClick={() => setExportOpen(true)}
                onOpenCalibration={() => setCalOpen(true)}
              />
            </div>

            {/* Side panels */}
            <div className="space-y-5">
              <div className="bg-surface border border-[#262626] rounded-xl p-4">
                <InstrumentalPanel
                  instrumentals={instrumentals}
                  onRefresh={loadInstrumentals}
                  engine={engine}
                  artistId={artist?.id}
                />
              </div>
              <div className="bg-surface border border-[#262626] rounded-xl p-4">
                <ExportsPanel
                  exports={exportsList}
                  orders={orders}
                  onRefresh={loadExports}
                  onDelivered={() => { loadOrders(); loadExports(); }}
                />
              </div>
              <div className="bg-surface border border-[#262626] rounded-xl p-4">
                <OrdersPanel
                  orders={orders}
                  exports={exportsList}
                  onDelivered={() => { loadOrders(); loadExports(); }}
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        engine={engine}
        onSaved={(doc) => {
          // show the new session in My Exports instantly, then reconcile
          if (doc?.id) setExportsList((prev) => [doc, ...prev.filter((e) => e.id !== doc.id)]);
          loadExports();
        }}
      />

      <LatencyCalibrationDialog open={calOpen} onOpenChange={setCalOpen} engine={engine} />
    </div>
  );
}
